/**
 * AccesosDenegados — La otra mitad de /auditoria (SEGURIDAD #4).
 *
 * La pestaña Actividad muestra lo que pasó. Ésta muestra lo que alguien quiso
 * que pasara y el servidor no dejó: los 401 y 403 que hasta ahora no quedaban
 * anotados en ninguna parte.
 *
 * Lo que hay que poder leer de un vistazo es UNA cosa: si los rechazos son gente
 * de la casa tocando un botón que no le toca (normal, y sirve para saber que a
 * alguien le falta un permiso) o alguien probando puertas (no normal). Por eso el
 * motivo manda: es el primer filtro y el color de cada fila.
 *
 * Sólo lo ve un admin — la ruta ya lo exige, esto no es la defensa.
 */
import React, { useEffect, useMemo, useState } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";

const MOTIVO_META = {
  rol_insuficiente:   { label: "Sin permiso",      color: "#f39c12", ayuda: "Sesión válida, rol que no alcanza" },
  sin_token:          { label: "Sin sesión",       color: "#3498db", ayuda: "Llegó sin token" },
  token_invalido:     { label: "Token inválido",   color: "#e74c3c", ayuda: "Firma mala o token malformado" },
  token_portal:       { label: "Token de portal",  color: "#a78bfa", ayuda: "Token del portal de clientes usado contra el ERP" },
  // SEGURIDAD #14. El override NO es un rechazo: va en verde y se dice, para que nadie
  // lo lea como un ataque cuando es una herramienta interna haciendo su trabajo.
  tenant_no_coincide: { label: "Otro inquilino",   color: "#e74c3c", ayuda: "Pidió datos de un tenant que no es el suyo" },
  tenant_override:    { label: "Override de tenant", color: "#2ecc71", ayuda: "Un admin miró otro tenant — permitido, queda anotado" },
};

const fmtFecha = iso => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-CL", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const AccesosDenegados = () => {
  const isMobile = useIsMobile();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [dias, setDias] = useState(30);
  const [motivo, setMotivo] = useState("all");
  const [usuario, setUsuario] = useState("all");

  useEffect(() => {
    setCargando(true);
    setError(null);
    backend.get(`auditoria/accesos?dias=${dias}&limit=500`)
      .then(({ data }) => setDatos(data))
      .catch(e => setError(e?.response?.data?.error || e.message))
      .finally(() => setCargando(false));
  }, [dias]);

  // El filtrado por motivo/usuario es local: el endpoint ya trajo el rango
  // completo y volver al server por cada chip sólo agregaría espera.
  const filas = useMemo(() => {
    let rows = datos?.filas || [];
    if (motivo !== "all") rows = rows.filter(f => f.motivo === motivo);
    if (usuario !== "all") rows = rows.filter(f => (f.usuario || "(sin sesión)") === usuario);
    return rows;
  }, [datos, motivo, usuario]);

  const usuarios = useMemo(() => {
    const set = new Set((datos?.filas || []).map(f => f.usuario).filter(Boolean));
    return [...set].sort();
  }, [datos]);

  const resumen = datos?.resumen;

  return (
    <div>
      {/* ── Filtros ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <select value={dias} onChange={e => setDias(Number(e.target.value))} style={selectStyle}>
          <option value={7}>Últimos 7 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={90}>Últimos 90 días</option>
          <option value={180}>Últimos 180 días</option>
        </select>
        <select value={usuario} onChange={e => setUsuario(e.target.value)} style={selectStyle}>
          <option value="all">Todos los usuarios</option>
          {usuarios.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>

      {/* ── Tarjetas por motivo (clickables = filtro) ── */}
      <div className="chips-scroll" style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Card label="Total" value={datos?.total ?? 0} color="#3498db"
          onClick={() => setMotivo("all")} active={motivo === "all"} />
        {(resumen?.porMotivo || []).map(m => {
          const meta = MOTIVO_META[m.motivo] || { label: m.motivo, color: "#cbd5e1", ayuda: "" };
          return (
            <Card key={m.motivo} label={meta.label} value={m.n} color={meta.color}
              nota={m.repetidos ? `+${m.repetidos} repetidos` : meta.ayuda}
              onClick={() => setMotivo(p => (p === m.motivo ? "all" : m.motivo))}
              active={motivo === m.motivo} />
          );
        })}
      </div>

      {/* El contador de rutas sin catalogar no es un error del usuario: es el aviso
          de que backend/config/accionesAuditoria.js quedó atrás de server.js. */}
      {!!resumen?.sinCatalogar && (
        <div style={{
          background: "#f39c1216", border: "1px solid #f39c1255", borderRadius: 6,
          padding: "8px 12px", marginBottom: 12, fontSize: 11, color: "#f39c12",
        }}>
          {resumen.sinCatalogar} rechazo{resumen.sinCatalogar === 1 ? "" : "s"} en rutas que no están en el
          catálogo de acciones. Hay que agregar el módulo nuevo a <code>backend/config/accionesAuditoria.js</code>.
        </div>
      )}

      {/* ── Top de lo más rechazado ── */}
      {!!resumen?.porAccion?.length && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {resumen.porAccion.slice(0, 8).map(a => (
            <span key={a.accion} style={{
              fontSize: 11, color: "#cbd5e1", background: "#242424",
              border: "1px solid #2e2e2e", borderRadius: 12, padding: "3px 10px",
            }}>{a.label} <b style={{ color: "#fff" }}>{a.n}</b></span>
          ))}
        </div>
      )}

      {cargando && <div style={{ padding: 16, textAlign: "center", color: "#aaa", fontSize: 12 }}>Cargando…</div>}
      {error && <div style={{ padding: 16, textAlign: "center", color: "#e74c3c", fontSize: 12 }}>{error}</div>}
      {!cargando && !error && filas.length === 0 && (
        <div style={{ padding: 20, textAlign: "center", color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
          Sin accesos denegados en el período. Es la lectura buena.
        </div>
      )}

      {!cargando && !error && filas.length > 0 && (isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filas.map(f => {
            const meta = MOTIVO_META[f.motivo] || { label: f.motivo, color: "#cbd5e1" };
            return (
              <div key={f.id} style={{
                background: "#232323", border: "1px solid #2e2e2e",
                borderRadius: 12, padding: "12px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge color={meta.color}>{meta.label}</Badge>
                  <span style={{
                    flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", color: "#fff", fontSize: 12,
                  }}>{f.accionLabel}</span>
                </div>
                <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 6, fontFamily: "monospace" }}>
                  {f.metodo} {f.ruta}
                </div>
                <div style={{ fontSize: 11, color: "#b8b8b8", marginTop: 4 }}>
                  {f.usuario || <i style={{ color: "#777" }}>sin sesión</i>}
                  {f.tenantHeader
                    ? ` · su tenant ${f.tenant || "—"} · pidió ${f.tenantHeader}`
                    : f.rolesRequeridos?.length
                      ? ` · tenía ${(f.rolesUsuario || []).join(", ") || "—"} · pedía ${f.rolesRequeridos.join(", ")}`
                      : ""}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6, fontSize: 11, color: "#8a8a8a" }}>
                  <span>{f.ip}</span>
                  <span style={{ flex: 1 }} />
                  {!!f.repetidos && <span style={{ color: "#f39c12" }}>+{f.repetidos} iguales</span>}
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtFecha(f.fecha)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, background: "#242424", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
            <thead>
              <tr style={{ background: "#1a1a1a" }}>
                <Th>Motivo</Th>
                <Th>Quién</Th>
                <Th>Intentó</Th>
                <Th>Ruta</Th>
                <Th>Tenía → pedía</Th>
                <Th>Origen</Th>
                <Th align="right">Cuándo</Th>
              </tr>
            </thead>
            <tbody>
              {filas.map(f => {
                const meta = MOTIVO_META[f.motivo] || { label: f.motivo, color: "#cbd5e1" };
                return (
                  <tr key={f.id} style={{ borderTop: "1px solid #1f1f1f" }}>
                    <Td><Badge color={meta.color}>{meta.label}</Badge></Td>
                    <Td>
                      <span style={{ fontSize: 11, color: "#ddd" }}>
                        {f.usuario || <i style={{ color: "#777" }}>sin sesión</i>}
                      </span>
                      {f.vistaComo && (
                        <span style={{ fontSize: 10, color: "#f39c12", marginLeft: 6 }}>
                          (viendo como {f.vistaComo})
                        </span>
                      )}
                    </Td>
                    <Td><span style={{ fontSize: 11, color: "#fff" }}>{f.accionLabel}</span></Td>
                    <Td>
                      <span style={{ fontSize: 11, color: "#aaa", fontFamily: "monospace" }}>
                        {f.metodo} {f.ruta}
                      </span>
                    </Td>
                    <Td>
                      <span style={{ fontSize: 11, color: "#cbd5e1" }}>
                        {f.tenantHeader
                          ? `${f.tenant || "—"} → pidió ${f.tenantHeader}`
                          : f.rolesRequeridos?.length
                            ? `${(f.rolesUsuario || []).join(", ") || "—"} → ${f.rolesRequeridos.join(", ")}`
                            : "—"}
                      </span>
                    </Td>
                    <Td>
                      <span style={{ fontSize: 11, color: "#aaa", fontFamily: "monospace" }}>{f.ip || "—"}</span>
                      {!!f.repetidos && (
                        <span style={{ fontSize: 10, color: "#f39c12", marginLeft: 6 }}>+{f.repetidos}</span>
                      )}
                    </Td>
                    <Td align="right">
                      <span style={{ fontSize: 11, color: "#cbd5e1", fontVariantNumeric: "tabular-nums" }}>
                        {fmtFecha(f.fecha)}
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {!cargando && !error && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
          {filas.length} de {datos?.filas?.length || 0} filas traídas
          {(datos?.total || 0) > (datos?.filas?.length || 0)
            ? ` · ${datos.total} en total en el período (se muestran las más recientes)`
            : ""}
          . Los tokens vencidos no se anotan: son el latido normal del refresh.
        </div>
      )}
    </div>
  );
};

const Badge = ({ color, children }) => (
  <span style={{
    fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 9,
    background: color + "22", color, border: `1px solid ${color}55`, whiteSpace: "nowrap",
  }}>{children}</span>
);

const Card = ({ label, value, color, nota, onClick, active }) => (
  <div onClick={onClick} className="audi-stat tap-card" style={{
    flex: "1 1 0", minWidth: 130, padding: "10px 14px",
    background: active ? `${color}22` : "#242424",
    border: `1px solid ${active ? color : "#2e2e2e"}`,
    borderRadius: 6, cursor: "pointer",
  }}>
    <div style={{ fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
    {nota && <div style={{ fontSize: 9, color: "#777", marginTop: 2 }}>{nota}</div>}
  </div>
);

const Th = ({ children, align = "left" }) => (
  <th style={{
    padding: "8px 12px", textAlign: align, fontSize: 9, fontWeight: 700,
    color: "#aaa", textTransform: "uppercase", letterSpacing: ".06em",
    borderBottom: "1px solid #2e2e2e", whiteSpace: "nowrap",
  }}>{children}</th>
);

const Td = ({ children, align = "left" }) => (
  <td style={{ padding: "8px 12px", textAlign: align, fontSize: 12, color: "#bbb", verticalAlign: "middle" }}>{children}</td>
);

const selectStyle = {
  background: "#161616", border: "1px solid #333", borderRadius: 5,
  padding: "6px 10px", fontSize: 12, color: "#ddd",
  fontFamily: "Arial,sans-serif", cursor: "pointer",
};

export default AccesosDenegados;
